import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CONSTANTS } from '../../../../Constants/Constants';
import { PreferencesOptionId } from '../../../../models/enum';
import { PropertyOption } from '../../../../models/properties/PropertyOptions';
import { SetUserPreferencesRequest } from '../../../../models/dtos';
import { PreferenceApiService } from '../../../../services/api/preference.service';
import { SolPreferencesCollaboratorsComponent } from './sol-preferences-collaborators/sol-preferences-collaborators.component';
import { SolPreferencesCostReviewersComponent } from './sol-preferences-cost-reviewers/sol-preferences-cost-reviewers.component';
import { SolPreferencesContactInfoComponent } from './sol-preferences-contact-info/sol-preferences-contact-info.component';
import { SolPreferencesEstimatorComponent } from './sol-preferences-estimator/sol-preferences-estimator.component';
import { SolPreferencesDisplayUnitsComponent } from './sol-preferences-display-units/sol-preferences-display-units.component';
import { SolPreferencesService } from '../../../../services/preferences/sol-preferences.service';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { SolPreferencesCostReviewCcComponent } from './sol-preferences-cost-review-cc/sol-preferences-cost-review-cc.component';
import { ReportsCommonService } from '../../../../services/reports/reports-common/reports-common.service';
import { HasUnsavedChanges, registerUnsavedChanges } from '../../../../services/common/unsaved-changes.service';

@Component({
  selector: 'app-sol-preferences',
  standalone: true,
  imports: [
    CommonModule,
    MatListModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    MatDividerModule,
    TranslatePipe,
    SolPreferencesCollaboratorsComponent,
    SolPreferencesCostReviewersComponent,
    SolPreferencesContactInfoComponent,
    SolPreferencesEstimatorComponent,
    SolPreferencesDisplayUnitsComponent,
    SolPreferencesCostReviewCcComponent
  ],
  templateUrl: './sol-preferences.component.html',
  styleUrl: './sol-preferences.component.scss',
  providers: [SolPreferencesService]
})
export class SolPreferencesComponent implements OnInit, HasUnsavedChanges {
  @Output() cancel = new EventEmitter<void>();

  selectedOptionId: string = CONSTANTS.EMPTY_STRING;
  preferencesOptionId = PreferencesOptionId;

  preferencesOptions: PropertyOption[] = [
    {
      id: PreferencesOptionId.COLLABORATORS,
      label: 'COLLABORATORS',
      isVisible: true
    },
    {
      id: PreferencesOptionId.COST_REVIEWERS,
      label: 'COST_REVIEWERS',
      isVisible: true
    },
    {
      id: PreferencesOptionId.COST_REVIEW_CC,
      label: 'COST_REVIEW_CC',
      isVisible: true
    },
    {
      id: PreferencesOptionId.CONTACT_INFO,
      label: 'CONTACT_INFO',
      isVisible: true
    },
    {
      id: PreferencesOptionId.ESTIMATOR,
      label: 'ESTIMATOR',
      isVisible: true
    },
    {
      id: PreferencesOptionId.DISPLAY_UNITS,
      label: 'DISPLAY_UNITS',
      isVisible: true
    }
  ];

  constructor(
    private readonly preferencesService: SolPreferencesService,
    private readonly preferenceApiService: PreferenceApiService,
    private readonly userProfileService: UserProfileService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly reportsCommonService: ReportsCommonService
  ) {
    registerUnsavedChanges(this);
  }

  ngOnInit(): void {
    this.preferencesService.getUserPreferencesApiResponse().then(() => {
      this.selectedOptionId = this.preferencesOptions[0].id;
    });
  }

  hasUnsavedChanges(): boolean {
    return this.preferencesService.hasChanges();
  }

  selectOption(optionId: string): void {
    this.selectedOptionId = optionId;
  }

  getSelectedOption(): PropertyOption | undefined {
    return this.preferencesOptions.find(option => option.id === this.selectedOptionId);
  }

  /** @returns true when the preferences were saved. */
  async save(): Promise<boolean> {

    // The shared service already holds the latest state —
    // each child tab writes its changes directly to the service.
    const current = this.preferencesService.getUserPreferences();

    const request: SetUserPreferencesRequest = {
      userId: this.userProfileService.userInfo.gid, // TO-DO: Replace with dynamic user ID
      preferences: current.preferences,
      firePreferences: current.firePreferences,
      esPreferences: current.esPreferences,
      proposalPreferences: current.proposalPreferences,
      collaboratorPreferences: current.collaboratorPreferences,
      costReviewerPreferences: current.costReviewerPreferences,
      taxDetailsPreferences: current.taxDetailsPreferences,
      resetPreferencesToDefault: false
    };

    try {
      const success = await firstValueFrom(this.preferenceApiService.setPreference(request));
      if (!success) {
        return false;
      }
      this.preferencesService.captureBaseline();
      this.notificationMessageService.showNotificationMessageDialog(
        CONSTANTS.TYPE_SUCCESS,
        'SUCCESS',
        'PREFERENCES_SAVED_SUCCESSFULLY',
        CONSTANTS.DEFAULT_DIALOG_WIDTH
      );
      this.reportsCommonService.notifyPreferencesSaved();
      return true;
    } catch {
      return false;
    }
  }

  cancelPreferences(): void {
    this.cancel.emit();
  }
}
